import { type FunctionComponent, type ReactElement, useMemo } from 'react';
import cn from 'classnames';
import {
    MaskedTextInput as OldMaskedTextInput,
    type MaskedTextInputProps,
    Text,
    TextInput as OldTextInput,
    type TextInputProps,
    VStack,
} from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import styles from './TextInput.module.css';

interface CustomTextInputBodyPayload {
    isFocused: boolean;
    setFocused: VoidFunction;
    unsetFocused: VoidFunction;
}

type CustomTextInputBody = Pick<TextInputProps, 'label' | 'labelId' | 'id' | 'value' | 'validationState'> & {
    children: (payload: CustomTextInputBodyPayload) => ReactElement;
};

export const CustomTextInputBody: FunctionComponent<CustomTextInputBody> = ({
    labelId,
    label,
    id,
    value,
    validationState,
    children,
}) => {
    const { isOpen: isInputFocused, setTruthy: setFocused, setFalsy: unsetFocused } = useToggle();

    const isFocused = useMemo(() => Boolean(isInputFocused || value), [isInputFocused, value]);

    return (
        <VStack
            gap="2"
            className={cn(styles.container, {
                [styles.active]: isFocused,
                [styles.focused]: isInputFocused,
                [styles.invalid]: validationState,
            })}
        >
            <label id={labelId} aria-label={label} htmlFor={id} className={styles.label}>
                <Text size="m" color="current" className={styles.labelText}>
                    {label}
                </Text>
            </label>

            {children({ isFocused, setFocused, unsetFocused })}
        </VStack>
    );
};

export const TextInput: FunctionComponent<TextInputProps> = ({ placeholder, ...props }) => (
    <CustomTextInputBody {...props}>
        {({ isFocused, setFocused, unsetFocused }) => (
            <OldTextInput
                className={styles.input}
                {...props}
                placeholder={isFocused ? placeholder : ''}
                onFocus={setFocused}
                onBlur={unsetFocused}
            />
        )}
    </CustomTextInputBody>
);

export const MaskedTextInput: FunctionComponent<MaskedTextInputProps> = ({ placeholder, ...props }) => (
    <CustomTextInputBody {...props}>
        {({ isFocused, setFocused, unsetFocused }) => (
            <OldMaskedTextInput
                className={styles.input}
                {...props}
                placeholder={isFocused ? placeholder : ''}
                onFocus={setFocused}
                onBlur={unsetFocused}
            />
        )}
    </CustomTextInputBody>
);
